import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { AppExclusiveDealLabel } from 'components/AppExclusiveDealLabel';
import { useShowTotalPriceGhaOrGvrPreferredRate } from 'components/Offer/hooks/useShowTotalPriceGhaOrGvrPreferredRate';
import { GHAPreferredRateTotalPrice } from 'components/Offer/Shared/OfferPrice/GHAPreferredRateTotalPrice';
import { SplitBookingBundleType } from 'components/Offer/types';
import { usePriceUtils } from 'hooks/usePriceUtils';
import { getCurrencyCode, getLocalTaxesIncluded, getTaxesIncluded } from 'modules/meta/selectors';
import { getAppLockedDealConfig, getShowTotalPrices, getShowTotalPriceToggle } from 'modules/sapiSearch/selectors';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Text } from '@daedalus/atlas/Text';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import getIsBofhOffer from '@daedalus/core/src/offer/business/isBofhOffer';
import { OfferPrimaryLayoutVariant } from '@daedalus/core/src/offer/types/OfferLayout';
import { OfferVariant } from '@daedalus/core/src/offer/types/OfferVariant';
import { SearchOffer } from '@daedalus/core/src/offer/types/SearchOffer';
import { useVioTag } from '@daedalus/core/src/tag';
import { TaxationAndNightlyPriceMessage as TaxationAndNightlyPriceComponent } from '@daedalus/shared/src/search/TaxationAndNightlyPriceMessage';
import { OfferPrice } from '../../Shared/OfferPrice';
interface MainProps {
  hotelId: string;
  showSignInToUnlock?: boolean;
  anchorPrice: number;
  offer: SearchOffer;
  offerVariant?: OfferVariant;
  anchorPriceTotal?: number;
  shouldShowTaxationAndNightly?: boolean;
  variant: OfferPrimaryLayoutVariant;
  showPriceIndication?: boolean;
  shouldShowTaxation?: boolean;
  isRequiredOneTimePasswordFlow?: boolean;
  isSplitBooking?: false;
  splitBookingType?: never;
  forceMobileLayout?: boolean;
}
interface SplitBookingProps {
  isSplitBooking: boolean;
  splitBookingType: SplitBookingBundleType;
  hotelId: string;
  shouldShowTaxationAndNightly?: boolean;
  variant: OfferPrimaryLayoutVariant;
  shouldShowTaxation?: boolean;
  showPriceIndication?: never;
  showSignInToUnlock?: never;
  anchorPrice?: number;
  anchorPriceTotal?: number;
  isRequiredOneTimePasswordFlow?: never;
  offer?: never;
  offerVariant?: never;
  forceMobileLayout?: boolean;
}
const getOfferPriceComponentLayout = (variant: OfferPrimaryLayoutVariant, isLongPrice: boolean, horizontally: boolean, shouldWrapAnchorPrice?: boolean): 'row' | 'column' | 'default' => {
  if (!['PrimaryMultiColumn', 'PrimaryMultiColumnFullWidthCTA'].includes(variant)) {
    return 'default';
  }
  if (variant === 'PrimaryMultiColumn') {
    if (shouldWrapAnchorPrice) return 'column';
    if (!isLongPrice) return 'row';
    return 'column';
  }
  if (variant === 'PrimaryMultiColumnFullWidthCTA') {
    if (shouldWrapAnchorPrice) return 'column';
    if (!isLongPrice && horizontally) return 'row';
    return 'column';
  }
  return 'default';
};
const _exp = /*#__PURE__*/() => OfferPrice;
const OfferPriceComponent = /*#__PURE__*/styled(_exp())({
  name: "OfferPriceComponent",
  class: "o78lwfu",
  propsAsIs: true
});
const Wrapper = /*#__PURE__*/styled('span')({
  name: "Wrapper",
  class: "wq7p4q6",
  propsAsIs: false
});
export const PriceComponent: React.FC<MainProps | SplitBookingProps> = ({
  isSplitBooking,
  splitBookingType = 'split_flow',
  hotelId,
  showSignInToUnlock = false,
  anchorPrice = 0,
  offer,
  offerVariant,
  anchorPriceTotal = 0,
  shouldShowTaxation = true,
  shouldShowTaxationAndNightly,
  variant = 'PrimaryDefault',
  showPriceIndication = false,
  isRequiredOneTimePasswordFlow,
  forceMobileLayout
}) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    isAppLockedDeal
  } = useSelector(state => getAppLockedDealConfig(state, hotelId, offer?.id));
  const includeTaxes = useSelector(getTaxesIncluded);
  const includeLocalTaxes = useSelector(getLocalTaxesIncluded);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle);
  const currencyCode = useSelector(getCurrencyCode);
  const {
    getPriceDetails
  } = usePriceUtils(forceMobileLayout);
  const {
    isLongPrice,
    shouldWrapAnchorPrice
  } = getPriceDetails(showTotalPrices ? offer?.totalRate : offer?.nightlyRate, isMobile);
  const shouldShowGhaOrGvrPreferredRateTotalPrice = useShowTotalPriceGhaOrGvrPreferredRate(offer, hotelId, isRequiredOneTimePasswordFlow);
  const priceSize = isLongPrice && !shouldWrapAnchorPrice ? 'md' : 'xl';
  const gridAlignment = useMemo<'flex-start' | 'flex-end'>(() => {
    if (variant === 'PrimaryDefault') return 'flex-start';
    return 'flex-end';
  }, [variant]);
  const gridJustifyContent = useMemo<'flex-start' | 'flex-end'>(() => {
    if (variant === 'PrimaryDefault') {
      return 'flex-start';
    }
    return 'flex-end';
  }, [variant]);
  const {
    brandCode
  } = useBrand();
  const isBofhOffer = getIsBofhOffer(offer);
  const {
    isVioTagVisible: isVioTagEligible
  } = useVioTag({
    price: showTotalPrices ? offer?.totalRate : offer?.nightlyRate,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    isVioOffer: isBofhOffer,
    brandCode
  });
  const isVioTagVisible = isVioTagEligible && offerVariant === OfferVariant.OnlyOnVio;
  const renderTaxation = useMemo(() => {
    if (!shouldShowTaxation) return;
    if (shouldShowTaxationAndNightly) {
      if (showTotalPrices) {
        return <Wrapper>
            {variant !== 'PrimaryDefault' && <>
                <FormattedMessageWrapper id="total" defaultMessage="total" />
                &nbsp;
              </>}
            <FormattedMessageWrapper id="includingTaxes" defaultMessage="incl tax and fees" />
          </Wrapper>;
      } else if (variant !== 'PrimaryDefault') {
        return <TaxationAndNightlyPriceComponent includeTaxes={includeTaxes || includeLocalTaxes} />;
      }
    }
    return <>
        <TaxationAndNightlyPriceComponent showPriceMessage={false} includeTaxes={includeTaxes || includeLocalTaxes} />
      </>;
  }, [includeLocalTaxes, includeTaxes, shouldShowTaxation, shouldShowTaxationAndNightly, showTotalPrices, variant]);
  const renderOfferPriceVariants = () => {
    if (!showTotalPriceToggle) {
      return <OfferPriceComponent className={cx(`--variant-${variant}`, isLongPrice && '--longPrice', `--${getOfferPriceComponentLayout(variant, isLongPrice, shouldWrapAnchorPrice)}`)} showLockIcon={isAppLockedDeal} showRowLayout={false} shouldDisplayInline={false} offer={offer} isPrivateDeal={isVioTagVisible} anchorPrice={anchorPrice} hotelId={hotelId} size={priceSize} showPriceIndication={showPriceIndication} />;
    }
    return <OfferPriceComponent className={cx(`--variant-${variant}`, isLongPrice && '--longPrice', `--${getOfferPriceComponentLayout(variant, isLongPrice, shouldWrapAnchorPrice)}`)} showRowLayout showLockIcon={isAppLockedDeal} anchorPriceTotal={anchorPriceTotal} anchorPrice={anchorPrice} offer={offer} isPrivateDeal={isVioTagVisible} showPriceIndication={showPriceIndication} hotelId={hotelId} size={priceSize} isRequiredOneTimePasswordFlow={isRequiredOneTimePasswordFlow} />;
  };
  if (isSplitBooking) {
    return <>
        <Grid container alignItems={gridAlignment} direction="column" justify={gridJustifyContent} spacing="s200">
          <div data-id="Price">
            <OfferPriceComponent className={cx(`--variant-${variant}`, isLongPrice && '--longPrice', `--${getOfferPriceComponentLayout(variant, isLongPrice, shouldWrapAnchorPrice)}`)} isSplitBooking hotelId={hotelId} showAnchorPrice={splitBookingType === 'single_flow'} anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} showPriceIndication={shouldShowTaxationAndNightly && variant === 'PrimaryDefault'} size={priceSize} />
          </div>
        </Grid>
        <Box textAlign={variant === 'PrimaryDefault' ? 'left' : 'right'}>
          {renderTaxation}
        </Box>
      </>;
  }
  return <>
      <Grid container alignItems={gridAlignment} direction="column" justify={gridJustifyContent} spacing="s200">
        {showSignInToUnlock && <Box paddingRight="s200" marginBottom="s250">
            <Text variant="labelXS" colorPath="content.special.c600">
              <FormattedMessageWrapper id="signInToUnlockLabel" defaultMessage="Sign in to unlock" />
            </Text>
          </Box>}

        {isRequiredOneTimePasswordFlow && <Box paddingRight="s200" marginBottom="s250" textAlign="right">
            <Text variant="labelXS" colorPath="content.special.c600">
              <FormattedMessageWrapper id="oneTimePasswordFlow.offerLabel" defaultMessage="Unlock with SMS" />
            </Text>
          </Box>}

        {isAppLockedDeal && <Box marginBottom="s200">
            <AppExclusiveDealLabel alignText="end" />
          </Box>}

        <div data-id="Price">{renderOfferPriceVariants()}</div>
      </Grid>
      <Box textAlign={variant === 'PrimaryDefault' ? 'left' : 'right'}>
        {renderTaxation}
      </Box>
      {shouldShowGhaOrGvrPreferredRateTotalPrice && <GHAPreferredRateTotalPrice textVariant="labelXS" value={offer.totalRate} currency={currencyCode as string} />}
    </>;
};

require("./PriceComponent.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PriceComponent.tsx");